import React, { useState, useEffect, useCallback, useMemo } from 'react';
import SettingsIcon from '@material-ui/icons/Settings';
import { Button, Grid } from '@material-ui/core';
import {
  endOfMonth,
  subMonths,
  startOfMonth,
  format,
  subQuarters,
  startOfQuarter,
  endOfQuarter,
} from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { NkDatePicker, NkSelect } from '@nordkap/nordkap-ui';
import { enGB, nb, sv } from 'date-fns/locale';
import { isValidDate } from '../../utils/date';
import appInsights from '../../infrastructure/appInsights';
import api from '../../infrastructure/api/api';
import { useStyles } from './styles';
import authCookie from '../../infrastructure/api/auth-cookie';
import { combineClasses } from '../../utils/styling';

const eventTypes = [
  'Accrued',
  'Amortization',
  'AmortizedFee',
  'Disbursement',
  'Fee',
  'FeeAmortization',
  'FinalPayment',
  'Payment',
  'PremiumOrDiscount',
  'PremiumOrDiscountAmortization',
  'Redemption',
  'TapIssue',
  'Capitalization',
];

const FileGeneration = () => {
  const [include, setInclude] = useState([...eventTypes]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [fileType, setFileType] = useState('');
  const [currencies, setCurrencies] = useState([]);
  const [currency, setCurrency] = useState('');
  const [portfoliosList, setPortfoliosList] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const { t } = useTranslation();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const dateRangeOptions = useMemo(
    () => [
      { value: '', title: t('global.form.none') },
      { value: 'ThisMonth', title: t('fileGenerationPage.thisMonth') },
      { value: 'LastMonth', title: t('fileGenerationPage.lastMonth') },
      {
        value: 'CurrentQuarter',
        title: t('fileGenerationPage.currentQuarter'),
      },
      {
        value: 'PreviousQuarter',
        title: t('fileGenerationPage.previousQuarter'),
      },
    ],
    [t]
  );

  const fileTypes = useMemo(
    () => [
      { value: '', title: t('global.form.none') },
      { value: 'PdfVoucherList', title: t('fileTypes.pdfVoucherList') },
      { value: 'ExcelVoucherList', title: t('fileTypes.excelVoucherList') },
      { value: 'Gl07', title: 'GL07 - Unit4 Business world' },
      { value: 'XIF', title: 'XIF - Visma Control' },
      { value: 'SIE4', title: 'SIE4 - Fortnox, Visma Business etc' },
      { value: 'Vitec', title: 'Excel - Vitec Ekonomi' },
      { value: 'Xpand', title: 'Excel - Xpand' },
    ],
    [t]
  );

  const languageCode = useMemo(
    () =>
      authCookie.getLangFromCookie() === 'en'
        ? 'en-GB'
        : authCookie.getLangFromCookie() || 'sv',
    []
  );

  const canDownload = useCallback(
    () =>
      isValidDate(fromDate) &&
      isValidDate(toDate) &&
      include != null &&
      include.length > 0 &&
      portfolios &&
      portfolios.length > 0 &&
      currency &&
      fileType,
    [currency, fileType, fromDate, portfolios, include, toDate]
  );

  const createDto = useCallback(
    () => ({
      include,
      fromDate: format(fromDate, 'yyyy-MM-dd'),
      toDate: format(toDate, 'yyyy-MM-dd'),
      fileType,
      currency,
      portfolios,
    }),
    [currency, fileType, fromDate, include, portfolios, toDate]
  );

  const downloadFile = useCallback(() => {
    const dto = createDto();
    return api.downloadFile('report', dto, enqueueSnackbar, closeSnackbar, t);
  }, [createDto, enqueueSnackbar, closeSnackbar, t]);

  const mapToCurrencyItem = useCallback(
    cur => ({
      value: cur,
      title: cur,
    }),
    []
  );

  useEffect(() => {
    appInsights.trackPageView();

    api.get('currencies', enqueueSnackbar, t).then(currenciesResult => {
      if (currenciesResult != null) {
        const currencyItems = currenciesResult.map(c => mapToCurrencyItem(c));
        const none = { value: '', title: <em>{t('global.form.none')}</em> };
        setCurrencies([none, ...currencyItems]);
      }
    });

    api.get('portfolios', enqueueSnackbar, t).then(portfoliosResult => {
      if (portfoliosResult != null) {
        setPortfoliosList(portfoliosResult);
      }
    });
  }, [enqueueSnackbar, mapToCurrencyItem, t]);

  // If there is only one currency available, automatically assign it
  useEffect(() => {
    if (currencies.length === 2) {
      setCurrency(currencies[1].value);
    }
  }, [currencies]);

  const handleChangeRange = useCallback(event => {
    switch (event.target.value) {
      case 'LastMonth': {
        const lastMonth = subMonths(new Date(), 1);
        setFromDate(startOfMonth(lastMonth));
        setToDate(endOfMonth(lastMonth));
        break;
      }
      case 'ThisMonth': {
        setFromDate(startOfMonth(new Date()));
        setToDate(endOfMonth(new Date()));
        break;
      }
      case 'PreviousQuarter': {
        const lastQuarter = subQuarters(new Date(), 1);
        setFromDate(startOfQuarter(lastQuarter));
        setToDate(endOfQuarter(lastQuarter));
        break;
      }
      case 'CurrentQuarter': {
        setFromDate(startOfQuarter(new Date()));
        setToDate(endOfQuarter(new Date()));
        break;
      }
      default:
        break;
    }
  }, []);

  const eventTypeToSelect = useCallback(
    eventType => ({
      value: eventType,
      title: t(`eventType.${eventType}`),
    }),
    [t]
  );

  const locales = { 'en-GB': enGB, nb, sv };
  const localeUtils = locales[languageCode];
  const dateOutOfBoundsMsg = (type, date) =>
    t(`global.form.date.${type}OutOfBounds`, {
      date: date
        ? format(date, localeUtils.formatLong.date(), { locale: localeUtils })
        : '',
    });

  const classes = useStyles();

  return (
    <div className={classes.Container}>
      <Grid container>
        <Grid
          item
          container
          xs={6}
          justifyContent="center"
          alignItems="stretch"
          spacing={3}
        >
          <Grid item xs={4}>
            <label
              className={combineClasses([
                classes.Bold,
                classes.DateLabel,
                classes.UpperCase,
              ])}
              htmlFor="date-range"
            >
              {t('fileGenerationPage.date')}
            </label>
            <NkSelect
              variant="outlined"
              name="date-range"
              itemPropName="value"
              itemTitlePropName="title"
              items={dateRangeOptions}
              value=""
              onChange={handleChangeRange}
            />
          </Grid>
          <Grid item xs={4}>
            <label
              className={combineClasses([
                classes.Bold,
                classes.DateLabel,
                classes.Required,
                classes.UpperCase,
              ])}
              htmlFor="date-from"
            >
              {t('fileGenerationPage.dateFrom')}
            </label>
            <NkDatePicker
              label=""
              variant="inline"
              inputVariant="outlined"
              name="date-from"
              value={fromDate}
              onChange={event => setFromDate(event.target.value)}
              isValid={isValidDate(fromDate)}
              languageCode={languageCode}
              required
            />
          </Grid>
          <Grid item xs={4}>
            <label
              className={combineClasses([
                classes.Bold,
                classes.DateLabel,
                classes.Required,
                classes.UpperCase,
              ])}
              htmlFor="date-to"
            >
              {t('fileGenerationPage.dateTo')}
            </label>
            <NkDatePicker
              label=""
              variant="inline"
              inputVariant="outlined"
              name="date-to"
              value={toDate}
              onChange={event => setToDate(event.target.value)}
              isValid={isValidDate(toDate)}
              languageCode={languageCode}
              min={fromDate}
              minDateOutOfBoundsMessage={dateOutOfBoundsMsg('min', fromDate)}
              required
            />
          </Grid>
          <Grid item xs={4}>
            <p
              className={combineClasses([
                classes.Bold,
                classes.Required,
                classes.UpperCase,
              ])}
            >
              {t('fileGenerationPage.include')}
            </p>
          </Grid>
          <Grid item xs={8}>
            <NkSelect
              variant="outlined"
              name="select-include"
              itemPropName="value"
              itemTitlePropName="title"
              required
              items={eventTypes.map(eventTypeToSelect)}
              value={include}
              onChange={event => setInclude(event.target.value)}
              isValid={include && include.length > 0}
              multiple
            />
          </Grid>
          <Grid item xs={4}>
            <p
              className={combineClasses([
                classes.Bold,
                classes.Required,
                classes.UpperCase,
              ])}
            >
              {t('fileGenerationPage.portfolios')}
            </p>
          </Grid>
          <Grid item xs={8}>
            <NkSelect
              variant="outlined"
              name="select-portfolios"
              itemPropName="name"
              itemTitlePropName="name"
              required
              items={portfoliosList}
              value={portfolios}
              onChange={event => setPortfolios(event.target.value)}
              isValid={portfolios && portfolios.length > 0}
              multiple
            />
          </Grid>
          <Grid item xs={4}>
            <p
              className={combineClasses([
                classes.Bold,
                classes.Required,
                classes.UpperCase,
              ])}
            >
              {t('fileGenerationPage.currency')}
            </p>
          </Grid>
          <Grid item xs={8}>
            <NkSelect
              variant="outlined"
              name="select-currency"
              itemPropName="value"
              itemTitlePropName="title"
              items={currencies}
              value={currency || ''}
              onChange={event => setCurrency(event.target.value)}
              isValid={currency !== ''}
              required
            />
          </Grid>
          <Grid item xs={4}>
            <p
              className={combineClasses([
                classes.Bold,
                classes.Required,
                classes.UpperCase,
              ])}
            >
              {t('fileGenerationPage.fileType')}
            </p>
          </Grid>
          <Grid item xs={8}>
            <NkSelect
              variant="outlined"
              name="select-filetype"
              itemPropName="value"
              itemTitlePropName="title"
              items={fileTypes}
              value={fileType || ''}
              onChange={event => setFileType(event.target.value)}
              isValid={fileType !== ''}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              id="download-button"
              variant="contained"
              color="primary"
              title={t('fileGenerationPage.generateFile')}
              onClick={downloadFile}
              disabled={!canDownload()}
            >
              <SettingsIcon />
              {t('fileGenerationPage.generateFile')}
            </Button>
          </Grid>
        </Grid>
        <Grid item xs />
      </Grid>
    </div>
  );
};

export default FileGeneration;
